import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, BellOff, Loader2 } from "lucide-react";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
const toKey = (b64) => {
  const s = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
};

export function PushToggle() {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const ok = supported();
  useEffect(() => {
    if (ok) navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {});
  }, [ok]);

  if (!ok) {
    if (!isIOS() || standalone()) return null;
    return <p className="mt-3 text-xs text-muted-foreground" data-testid="push-ios-hint"><Bell className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />En iPhone, instala la app en tu pantalla de inicio para recibir avisos de tus sesiones.</p>;
  }

  const enable = async () => {
    setBusy(true);
    try {
      if (await Notification.requestPermission() !== "granted") { toast.error("Debes permitir las notificaciones en tu navegador"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) });
      await api.post("/push/subscribe", s.toJSON());
      setSub(s);
      api.post("/push/test").catch(() => {});
      toast.success("Avisos activados en este celular");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No pudimos activar los avisos"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); setSub(null); toast.success("Avisos desactivados"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 flex flex-wrap sm:flex-nowrap items-center gap-3 rounded-2xl border p-3" data-testid="push-toggle-card">
      <Bell className="w-5 h-5 text-primary shrink-0" />
      <p className="flex-1 text-xs text-muted-foreground min-w-0 basis-[calc(100%-2.5rem)] sm:basis-auto">
        {sub ? "Te avisaremos en este celular 2 horas antes de cada sesión, con tu QR listo." : "Recibe un aviso en este celular 2 horas antes de cada sesión, con tu QR listo."}
      </p>
      <Button size="sm" variant={sub ? "ghost" : "default"} onClick={sub ? disable : enable} disabled={busy} className="shrink-0 w-full sm:w-auto" data-testid="push-toggle-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : sub ? <><BellOff className="w-4 h-4 mr-1" />Desactivar</> : "Activar avisos en este celular"}
      </Button>
    </div>
  );
}
